import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/apiClient";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Flag, Plus, Trophy, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

function StatPair({ label, own, comp, suffix = "", prefix = "" }) {
  return (
    <div className="rounded-sm border border-zinc-800 bg-zinc-950 p-3">
      <div className="overline mb-2 text-zinc-500">{label}</div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-emerald-400">Ours: <span className="font-mono font-bold">{prefix}{(own || 0).toLocaleString()}{suffix}</span></span>
        <span className="text-rose-400">Comp: <span className="font-mono font-bold">{prefix}{(comp || 0).toLocaleString()}{suffix}</span></span>
      </div>
    </div>
  );
}

export default function Competition() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [dash, setDash] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/competition/projects").then(({ data }) => setProjects(data));
    api.get("/competition/dashboard").then(({ data }) => setDash(data));
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!(form.name || "").trim()) { toast.error("Project name is required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/competition/projects", form);
      toast.success("Project created");
      setOpen(false); setForm({});
      navigate(`/competition/${data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create"); }
    finally { setSaving(false); }
  };

  const o = dash?.overall || {};

  return (
    <div className="space-y-6" data-testid="competition-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="overline flex items-center gap-2 text-rose-400"><Flag size={14} weight="fill" /> Separate from tyre inventory</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">Competition Tracking</h1>
          <p className="mt-1 text-sm text-zinc-500">Compare our tyres against competitor tyres in field trials.</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="new-project-btn" className="bg-amber-500 font-bold text-zinc-950 hover:bg-amber-400">
          <Plus size={16} weight="bold" className="mr-1" /> New Project
        </Button>
      </div>

      {dash && (dash.tyres > 0) && (
        <div className="rounded-sm border border-zinc-800 bg-zinc-900 p-5" data-testid="competition-dashboard">
          <h2 className="mb-4 flex items-center gap-2 font-display text-sm font-bold tracking-tight text-zinc-200"><Trophy size={16} className="text-amber-400" /> Overall — Ours vs Competitor</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatPair label="Tyres Tracked" own={o.own?.count} comp={o.competitor?.count} />
            <StatPair label="Avg KM Delivered" own={o.own?.avg_km} comp={o.competitor?.avg_km} suffix=" km" />
            <StatPair label="Avg CPKM" own={o.own?.cpkm} comp={o.competitor?.cpkm} prefix="₹" />
            <StatPair label="Avg Cost" own={o.own?.avg_cost} comp={o.competitor?.avg_cost} prefix="₹" />
          </div>
          {(dash.by_brand || []).length > 0 && (
            <div className="mt-5">
              <div className="overline mb-2 text-zinc-500">By Brand (avg KM)</div>
              <div className="overflow-x-auto rounded-sm border border-zinc-800">
                <table className="w-full text-xs">
                  <thead className="bg-zinc-950 text-left text-zinc-500"><tr>
                    <th className="px-3 py-2">Brand</th><th className="px-3 py-2">Side</th><th className="px-3 py-2">Tyres</th><th className="px-3 py-2">Avg KM</th><th className="px-3 py-2">Avg CPKM</th>
                  </tr></thead>
                  <tbody className="divide-y divide-zinc-800">
                    {dash.by_brand.map((b) => (
                      <tr key={b.brand} className="bg-zinc-900">
                        <td className="px-3 py-2 font-semibold text-zinc-200">{b.brand}</td>
                        <td className={`px-3 py-2 ${b.is_competitor ? "text-rose-400" : "text-emerald-400"}`}>{b.is_competitor ? "Competitor" : "Ours"}</td>
                        <td className="px-3 py-2 tabular-nums">{b.count}</td>
                        <td className="px-3 py-2 font-mono tabular-nums text-amber-500">{b.avg_km.toLocaleString()}</td>
                        <td className="px-3 py-2 font-mono tabular-nums">₹{b.cpkm.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {projects.map((p) => (
          <button key={p.id} data-testid={`project-${p.id}`} onClick={() => navigate(`/competition/${p.id}`)}
            className="group rounded-sm border border-zinc-800 bg-zinc-900 p-4 text-left ring-1 ring-white/5 transition-colors hover:border-amber-500">
            <div className="flex items-center justify-between">
              <div className="font-display text-base font-bold text-zinc-50 group-hover:text-amber-400">{p.name}</div>
              <ArrowRight size={16} className="text-zinc-600 group-hover:text-amber-400" />
            </div>
            <div className="mt-1 text-xs text-zinc-500">{p.site_name || "—"}{p.our_brand ? ` · Our brand: ${p.our_brand}` : ""}</div>
            <div className="mt-3 flex gap-4 text-xs">
              <span className="text-emerald-400">Ours: <span className="font-mono">{p.summary?.own?.count || 0}</span></span>
              <span className="text-rose-400">Comp: <span className="font-mono">{p.summary?.competitor?.count || 0}</span></span>
              <span className="text-zinc-400">{p.tyre_count || 0} tyre(s)</span>
            </div>
          </button>
        ))}
        {projects.length === 0 && (
          <div className="col-span-full rounded-sm border border-dashed border-zinc-800 p-8 text-center text-sm text-zinc-500">
            No competition projects yet. Create one to start tracking a field trial.
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="project-dialog">
          <DialogHeader><DialogTitle className="font-display">New Competition Project</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2"><Label className="text-xs text-zinc-400">Project Name *</Label>
              <Input data-testid="proj-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-zinc-950" /></div>
            <div><Label className="text-xs text-zinc-400">Mine Site / Location</Label>
              <Input data-testid="proj-site" value={form.site_name || ""} onChange={(e) => setForm({ ...form, site_name: e.target.value })} className="mt-1 bg-zinc-950" /></div>
            <div><Label className="text-xs text-zinc-400">Our Brand</Label>
              <Input data-testid="proj-brand" value={form.our_brand || ""} onChange={(e) => setForm({ ...form, our_brand: e.target.value })} className="mt-1 bg-zinc-950" /></div>
            <div><Label className="text-xs text-zinc-400">Contact Person</Label>
              <Input value={form.contact_person || ""} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="mt-1 bg-zinc-950" /></div>
            <div><Label className="text-xs text-zinc-400">Contact Number</Label>
              <Input value={form.contact_number || ""} onChange={(e) => setForm({ ...form, contact_number: e.target.value })} className="mt-1 bg-zinc-950" /></div>
            <div className="sm:col-span-2"><Label className="text-xs text-zinc-400">Application / Notes</Label>
              <Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 bg-zinc-950" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={saving} data-testid="proj-save" className="bg-amber-500 font-bold text-zinc-950 hover:bg-amber-400">{saving ? "Creating…" : "Create Project"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
